/**
 * Campus footprint — approved employee positions per campus, filled against vacant,
 * with the ICA and casual positions at the same campus beside them (M07, slide 8).
 *
 * THREE THINGS THIS PANEL EXISTS TO STOP A READER GETTING WRONG.
 *
 *  1. THESE ARE POSITIONS, NOT PEOPLE. The employee bar is the approved
 *     establishment at that campus — permanent and fixed-term positions, filled or
 *     standing empty. "Vacant" is every employee position with no one in post, not
 *     the reported vacancy figure (which leaves out capacity provisions and
 *     unfunded positions).
 *  2. CENTRAL AND CORPORATE PROGRAMMES ARE NOT CAMPUSES. They carry the functions
 *     that report nationally rather than to a site (`campus_type` 'national'), and
 *     Central is the largest row for exactly that reason, so it is called out on
 *     the face of the panel and marked on the axis.
 *  3. ICA AND CASUAL POSITIONS ARE NOT EMPLOYEE POSITIONS. The teal bar is drawn
 *     beside the employee bar because the footprint is not legible without it, but
 *     it is never added to it and never enters its vacancy rate.
 *
 * WHAT IDENTITY SERVES. Every count is a count of positions and is published in
 * full, as the board pack publishes M07: a campus's filled count is its positions
 * less its vacancies, so there is nothing to withhold. The codes the pack pools
 * into "Other / not yet assigned" arrive as ONE row (`CAMPUS_OTHER_CODE`), after
 * the campuses, with no vacancy rate, because M07 prints none for it. A vacancy
 * rate is shown only where identity sends one; it is never computed here.
 *
 * A key the response leaves out is not available for this date — a copy stored
 * before the finalised pack carries no `campus_type`, `sessional_total`, vacancy
 * rate or `totals` — and says so rather than reading as zero.
 */
import React, { useMemo, type ReactNode } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable, LegendSwatch } from './PanelShell';
import {
  AXIS,
  CAMPUS_OTHER_CODE,
  CATEGORY_COLORS,
  Def,
  GRID,
  INK,
  basisLabel,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type CampusFootprintRow,
  type CampusFootprintTotals,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. The footprint counts approved positions and
   *  is the same on every basis, which is what the window line states. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

// ---------------------------------------------------------------------------
// PALETTE — hex only, same family as apiContract CATEGORY_COLORS. Chart marks
// never take a Tailwind class.
// ---------------------------------------------------------------------------

const FILLED_COLOR = INK; // #15305D
const VACANT_COLOR = CATEGORY_COLORS.Unclassified; // #B9C4D6
const SESSIONAL_COLOR = CATEGORY_COLORS.ICA; // #0B7F72
const LABEL_GREY = '#6b7280';

/** On a copy stored before `campus_type`, the one national code it knew. */
const LEGACY_NATIONAL_CODE = 'CE';
/** On a copy stored before the Other row was pooled, positions with no campus carry this code. */
const LEGACY_NO_CAMPUS_CODE = '(no dimension)';

const NOT_AVAILABLE = 'not available for this date';

// ---------------------------------------------------------------------------
// DEFINITIONS — server definitions win: the lookup is only consulted when it misses.
// ---------------------------------------------------------------------------

const defBody = (label: string, body: string, caveats: string[] = []): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveats.length ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        {caveats.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FALLBACK_DEFS: Record<string, ReactNode> = {
  campus_footprint: defBody(
    'Campus footprint',
    'Approved permanent and fixed-term positions at each campus, split into filled and vacant, with '
      + 'the ICA and casual positions at the same campus shown beside them and never added to them.',
    [
      'Positions, not people.',
      'Central and Corporate Programmes are national functions, not campuses.',
      'ICA and casual positions are not employee positions.',
    ],
  ),
  campus_employee_positions: defBody(
    'Employee positions',
    'Approved permanent and fixed-term positions at that campus on the date shown, filled or vacant.',
  ),
  campus_filled: defBody(
    'Filled positions',
    'Employee positions with someone in post on the date shown.',
  ),
  campus_vacant: defBody(
    'Vacant positions',
    'Approved employee positions with no one in post on the date shown.',
    ['Not the reported vacancy figure, which leaves out capacity provisions and unfunded positions.'],
  ),
  campus_vacancy_rate: defBody(
    'Vacancy rate',
    'Vacant employee positions as a share of the employee positions at that campus, as the board pack '
      + 'states it.',
    ['Not shown for Other / not yet assigned, which the board pack shows without a rate.'],
  ),
  campus_sessional: defBody(
    'ICA and casual positions',
    'Approved ICA and casual positions at that campus, filled and vacant.',
    ['Not employee positions: never added to the employee bar or its vacancy rate.'],
  ),
  campus_national: defBody(
    'National functions',
    'Central and Corporate Programmes carry the functions that report nationally rather than to a '
      + 'campus.',
  ),
  campus_other: defBody(
    'Other / not yet assigned',
    'Positions with no campus recorded, and codes outside the fifteen campuses, shown together as the '
      + 'board pack shows them. Not a campus.',
  ),
};

/** Server definition first, local wording only where the server has none. */
function extendDefs(def: DefLookup, extra: Record<string, ReactNode>): DefLookup {
  return (key: string) => def(key) ?? extra[key];
}

// ---------------------------------------------------------------------------
// ROW MODEL
// ---------------------------------------------------------------------------

interface Row {
  code: string;
  name: string;
  /** Axis label; a national function is marked as one. */
  label: string;
  national: boolean;
  /** The pooled Other row, or positions with no campus recorded on an older copy. */
  other: boolean;
  total: number;
  filled: number;
  vacant: number;
  /** null: not available for this date (an older copy), so no bar is drawn. */
  sessionalTotal: number | null;
  sessionalFilled: number;
  /** Identity's rate, never one computed here. null: none served for this row. */
  vacancyPct: number | null;
  vacancyLabel: string;
  sessionalLabel: string;
}

/** "Central (national and shared services)" -> "Central", for the axis and the callout. */
const shortName = (name: string) => name.replace(/\s*\(.*\)\s*$/, '').trim() || name;

const joinNames = (names: string[]) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

function buildRows(campuses: CampusFootprintRow[]): Row[] {
  const typed = campuses.some((c) => c.campus_type !== undefined);
  const rows = campuses.map((c): Row => {
    const code = String(c.code ?? '').trim() || '—';
    const national = typed ? c.campus_type === 'national' : code === LEGACY_NATIONAL_CODE;
    const other = typed
      ? c.campus_type === 'other'
      : code === CAMPUS_OTHER_CODE || code === LEGACY_NO_CAMPUS_CODE;
    const name =
      code === LEGACY_NO_CAMPUS_CODE ? 'No campus recorded' : String(c.name ?? '').trim() || code;
    const vacancyPct = typeof c.employee_vacancy_pct === 'number' ? c.employee_vacancy_pct : null;
    const sessionalTotal = typeof c.sessional_total === 'number' ? c.sessional_total : null;
    return {
      code,
      name,
      label: national ? `${shortName(name)} — national` : name,
      national,
      other,
      total: c.employee_total,
      filled: c.employee_filled,
      vacant: c.employee_vacant,
      sessionalTotal,
      sessionalFilled: c.sessional_filled,
      vacancyPct,
      vacancyLabel: vacancyPct === null ? '' : `${fmtPct(vacancyPct)} vacant`,
      sessionalLabel: sessionalTotal === null ? '' : fmtNum(sessionalTotal),
    };
  });
  // The axis is a category scale: two rows sharing a label would land on one tick,
  // so a repeated label keeps its code. Served order is the board pack's; kept.
  const uses = new Map<string, number>();
  rows.forEach((r) => uses.set(r.label, (uses.get(r.label) ?? 0) + 1));
  return rows.map((r) => ((uses.get(r.label) ?? 0) > 1 ? { ...r, label: `${r.label} (${r.code})` } : r));
}

interface Totals extends Omit<CampusFootprintTotals, 'sessional_total'> {
  /** null: not available for this date. */
  sessional_total: number | null;
}

/** The served total row. A copy stored before it carries none: every row count is
 *  published, so the sum is exact — except ICA and casual positions, which such a
 *  copy does not carry, and which are then not available. */
function readTotals(served: CampusFootprintTotals | undefined, rows: Row[]): Totals {
  if (served) return served;
  const sum = (pick: (r: Row) => number) => rows.reduce((s, r) => s + pick(r), 0);
  return {
    employee_total: sum((r) => r.total),
    employee_filled: sum((r) => r.filled),
    employee_vacant: sum((r) => r.vacant),
    sessional_total: rows.every((r) => r.sessionalTotal !== null)
      ? sum((r) => r.sessionalTotal ?? 0)
      : null,
    sessional_filled: sum((r) => r.sessionalFilled),
  };
}

const vacancyRateText = (row: Row): string => {
  if (row.vacancyPct !== null) return fmtPct(row.vacancyPct);
  if (row.code === CAMPUS_OTHER_CODE) return 'none shown for this row';
  if (row.total === 0) return 'no employee positions';
  return NOT_AVAILABLE;
};

// ---------------------------------------------------------------------------
// TOOLTIP
// ---------------------------------------------------------------------------

interface TipProps {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
}

const TipLine: React.FC<{ label: string; value: ReactNode; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="flex items-center justify-between gap-4">
    <span className="flex items-center gap-1.5 text-gray-600">
      <span
        className="inline-block h-2 w-2 rounded-sm"
        style={{ background: color ?? 'transparent' }}
      />
      {label}
    </span>
    <span className="font-semibold tabular-nums text-heritage-blue">{value}</span>
  </div>
);

const FootprintTip: React.FC<TipProps> = ({ active, payload }) => {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <div className="max-w-[280px] rounded border border-gray-200 bg-white p-2.5 text-[11.5px] shadow-lg">
      <div className="mb-1 font-semibold text-heritage-blue">
        {row.name}
        {row.name === row.code || row.other ? '' : ` (${row.code})`}
      </div>
      {row.national ? (
        <div className="mb-1.5 text-[10.5px] leading-snug text-amber-700">
          A national function, not a campus site.
        </div>
      ) : null}
      {row.other ? (
        <div className="mb-1.5 text-[10.5px] leading-snug text-amber-700">
          Positions with no campus recorded, and codes outside the campuses — not a location.
        </div>
      ) : null}
      <div className="space-y-0.5">
        <TipLine label="Employee positions" value={fmtNum(row.total)} />
        <TipLine label="Filled" color={FILLED_COLOR} value={fmtNum(row.filled)} />
        <TipLine label="Vacant" color={VACANT_COLOR} value={fmtNum(row.vacant)} />
        <TipLine label="Vacancy rate" value={vacancyRateText(row)} />
        <TipLine
          label="ICA and casual positions"
          color={SESSIONAL_COLOR}
          value={row.sessionalTotal === null ? NOT_AVAILABLE : fmtNum(row.sessionalTotal)}
        />
        <TipLine label="of which filled" value={fmtNum(row.sessionalFilled)} />
      </div>
      <div className="mt-1.5 border-t border-gray-100 pt-1 text-[10.5px] leading-snug text-gray-500">
        ICA and casual positions are not employee positions and never enter the vacancy rate.
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PANEL
// ---------------------------------------------------------------------------

export const CampusFootprintPanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const d = useMemo(() => extendDefs(def, FALLBACK_DEFS), [def]);
  const block = readExtras(panels, 'campus_footprint');
  const rows = useMemo(
    () => buildRows(Array.isArray(block?.campuses) ? block.campuses : []),
    [block],
  );
  const totals = useMemo(() => readTotals(block?.totals, rows), [block, rows]);

  // The block's own date and kind, never panels_as_at.
  const dateLabel = panelDateLabel(panels, 'campus_footprint');

  const sites = rows.filter((r) => !r.national && !r.other && r.total > 0);
  const national = rows.filter((r) => r.national);
  const nationalNames = joinNames(national.map((r) => shortName(r.name)));
  const hasOther = rows.some((r) => r.other);
  const largest = rows.reduce<Row | null>((m, r) => (!m || r.total > m.total ? r : m), null);
  const chartHeight = Math.max(220, rows.length * 30 + 34);

  return (
    <PanelShell
      title="Campus footprint"
      defKey="campus_footprint"
      def={d}
      window={
        <>
          {dateLabel} — approved positions on that date. Counted on approved positions whatever
          basis is selected above
          {basis === 'positions' ? '' : ` (you are viewing ${basisLabel(basis).toLowerCase()})`}, so
          it does <b>not</b> follow the basis toggle.
        </>
      }
      right={
        rows.length ? (
          <div className="text-right">
            <div className="text-lg font-bold leading-none tabular-nums text-heritage-blue">
              {sites.length}
            </div>
            <div className="text-[10px] text-gray-500">
              campuses{nationalNames ? `, plus ${nationalNames}` : ''}
            </div>
          </div>
        ) : null
      }
      footnote={
        <>
          {block?.note ? <>{block.note} </> : null}
          Every figure here counts positions and is shown in full, as the board pack shows it.
          Vacant is every employee position with no one in post; the reported vacancies leave out
          capacity provisions and unfunded positions.
          {hasOther
            ? ' Other / not yet assigned pools the positions with no campus recorded and the codes '
              + 'outside the campuses, and carries no vacancy rate, as in the board pack.'
            : ''}{' '}
          Rows holding no positions are kept rather than dropped: a campus falling out of the
          establishment is itself the finding.
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={460} />
      ) : !panels ? (
        <PanelUnavailable what="The campus footprint" />
      ) : !rows.length ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          The campus footprint is not available for this date.
        </div>
      ) : (
        <div className="space-y-4">
          <TileRow min="150px">
            <MetricTile
              label="Employee positions"
              value={fmtNum(totals.employee_total)}
              defKey="campus_employee_positions"
              def={d}
              size="sm"
              sub="permanent and fixed-term, filled and vacant"
            />
            <MetricTile
              label="Filled"
              value={fmtNum(totals.employee_filled)}
              defKey="campus_filled"
              def={d}
              size="sm"
              sub="employee positions with someone in post"
            />
            <MetricTile
              label="Vacant"
              value={fmtNum(totals.employee_vacant)}
              defKey="campus_vacant"
              def={d}
              size="sm"
              sub="not the reported vacancy figure"
            />
            <MetricTile
              label="ICA and casual positions"
              value={
                totals.sessional_total === null ? (
                  <span className="text-gray-400" title="Not available for this date">
                    —
                  </span>
                ) : (
                  fmtNum(totals.sessional_total)
                )
              }
              defKey="campus_sessional"
              def={d}
              size="sm"
              sub={
                totals.sessional_total === null
                  ? `${NOT_AVAILABLE}; ${fmtNum(totals.sessional_filled)} filled`
                  : `${fmtNum(totals.sessional_filled)} filled — beside the employee positions, never added`
              }
            />
          </TileRow>

          {/* The national functions are the most misread rows on this panel. */}
          {national.length ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-900">
              <b>
                {nationalNames}{' '}
                {national.length === 1
                  ? 'is a national function, not a campus'
                  : 'are national functions, not campuses'}
                .
              </b>{' '}
              {national.length === 1 ? 'It carries' : 'They carry'} the functions that report
              nationally rather than to a site
              {largest?.national ? `, which is why ${shortName(largest.name)} is the largest row here` : ''}
              : {national.map((r) => `${shortName(r.name)} ${fmtNum(r.total)}`).join(', ')} employee
              positions. Read as {national.length === 1 ? 'a campus' : 'campuses'}, head-office
              establishment turns into campus growth.
              <Def def={d} k="campus_national" className="ml-1" />
            </div>
          ) : null}

          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Employee positions filled and vacant, ICA and casual positions beside them
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <LegendSwatch
                  color={FILLED_COLOR}
                  label="Filled positions"
                  defKey="campus_filled"
                  def={d}
                />
                <LegendSwatch
                  color={VACANT_COLOR}
                  label="Vacant positions"
                  defKey="campus_vacant"
                  def={d}
                />
                <LegendSwatch
                  color={SESSIONAL_COLOR}
                  label="ICA and casual positions"
                  defKey="campus_sessional"
                  def={d}
                />
              </div>
            </div>

            {/* Fixed-height wrapper: ResponsiveContainer has no height of its own. */}
            <div style={{ width: '100%', height: chartHeight }}>
              <ResponsiveContainer>
                <BarChart
                  data={rows}
                  layout="vertical"
                  margin={{ top: 4, right: 80, left: 4, bottom: 4 }}
                  barCategoryGap="22%"
                  barGap={1}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: AXIS }}
                    tickFormatter={(v: number) => fmtNum(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: AXIS }}
                    width={172}
                    interval={0}
                  />
                  <RechartsTooltip
                    cursor={{ fill: 'rgba(21, 48, 93, 0.06)' }}
                    content={<FootprintTip />}
                  />
                  <Bar
                    dataKey="filled"
                    stackId="employee"
                    name="Filled positions"
                    fill={FILLED_COLOR}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey="vacant"
                    stackId="employee"
                    name="Vacant positions"
                    fill={VACANT_COLOR}
                    isAnimationActive={false}
                  >
                    {/* Identity's vacancy rate where it sends one; none is computed here. */}
                    <LabelList
                      dataKey="vacancyLabel"
                      position="right"
                      fontSize={9.5}
                      fill={LABEL_GREY}
                    />
                  </Bar>
                  <Bar
                    dataKey="sessionalTotal"
                    stackId="sessional"
                    name="ICA and casual positions"
                    fill={SESSIONAL_COLOR}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="sessionalLabel"
                      position="right"
                      fontSize={9.5}
                      fill={LABEL_GREY}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {rows.some((r) => r.sessionalTotal === null) ? (
            <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
              ICA and casual positions are {NOT_AVAILABLE}
              {rows.every((r) => r.sessionalTotal === null) ? '' : ' at some campuses'}, so no teal bar
              is drawn there. That is not none.
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
